import {
  Button,
  Col,
  DatePicker,
  Form,
  Layout,
  Row,
  Select,
  Table,
  TablePaginationConfig,
  Tag,
  Typography,
  message,
} from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { ICampaignPromoCodesService } from "@shared/interfaces/campaign-promo-codes-service.interface";
import { ICampaignsService } from "@shared/interfaces/campaigns-service.interface";
import { CampaignPromoCodeDto } from "@shared/types/campaign-promo-codes/campaign-promo-code.dto";
import { parsePrefixedPromoCode } from "@shared/utils/campaign-promo-code";
import { formatTableDateTime } from "@shared/utils/format-datetime";
import GeneratePromoCodesModal from "./GeneratePromoCodesModal/GeneratePromoCodesModal";
import styles from "./CampaignPromoCodes.module.scss";

const ALL = "All";
const { RangePicker } = DatePicker;

const CampaignPromoCodes = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const promoService = appInject<ICampaignPromoCodesService>(
    DI_TOKENS.campaignPromoCodesService,
  );
  const campaignsService = appInject<ICampaignsService>(
    DI_TOKENS.campaignsService,
  );
  const queryClient = useQueryClient();

  const campaignFilter = searchParams.get("campaignId") ?? ALL;
  const statusFilter = searchParams.get("status") ?? ALL;
  const redeemedFrom = searchParams.get("redeemedFrom") ?? undefined;
  const redeemedTo = searchParams.get("redeemedTo") ?? undefined;
  const withCpaRedemption = searchParams.get("withCpaRedemption") === "true";

  const redeemedRange = useMemo((): [dayjs.Dayjs, dayjs.Dayjs] | null => {
    if (redeemedFrom && redeemedTo) {
      return [dayjs(redeemedFrom), dayjs(redeemedTo)];
    }
    return null;
  }, [redeemedFrom, redeemedTo]);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value == null || value === "") {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [currentPageSize, setPageSize] = useState(20);
  const [containerHeight, setContainerHeight] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);

  const { data: campaignsList } = useQuery(["campaigns-all"], () =>
    campaignsService.list({ page: 1, pageSize: 500 }),
  );
  const campaigns = campaignsList?.campaigns ?? [];

  const selectedCampaignId =
    campaignFilter === ALL ? undefined : campaignFilter;
  const selectedStatus =
    statusFilter === ALL
      ? undefined
      : (statusFilter as "used" | "unused");

  const campaignOptions = useMemo(
    () => [
      { label: "Filter by campaign", value: ALL },
      ...campaigns.map((c) => ({
        label: c.codePrefix ? `${c.name} (${c.codePrefix})` : c.name,
        value: c.id,
      })),
    ],
    [campaigns],
  );

  const statusOptions = [
    { label: "Filter by status", value: ALL },
    { label: "Used", value: "used" },
    { label: "Unused", value: "unused" },
  ];

  const listFilters = useMemo(
    () => ({
      campaignId: selectedCampaignId,
      status: selectedStatus,
      redeemedFrom,
      redeemedTo,
      withCpaRedemption: withCpaRedemption || undefined,
    }),
    [
      selectedCampaignId,
      selectedStatus,
      redeemedFrom,
      redeemedTo,
      withCpaRedemption,
    ],
  );

  const exportCsvMutation = useMutation(
    () => promoService.exportCsv(listFilters),
    {
      onSuccess: () => {
        void message.success("Promo codes exported");
      },
      onError: () => {
        void message.error("Failed to export promo codes");
      },
    },
  );

  const { data: promoList, isFetching } = useQuery(
    [
      "campaign-promo-codes",
      selectedCampaignId,
      selectedStatus,
      redeemedFrom,
      redeemedTo,
      withCpaRedemption,
      page,
      currentPageSize,
    ],
    () =>
      promoService.list({
        ...listFilters,
        page,
        pageSize: currentPageSize,
      }),
  );

  const columns: ColumnsType<CampaignPromoCodeDto> = useMemo(
    () => [
      ...(campaignFilter === ALL
        ? [
            {
              title: "Campaign",
              key: "campaign",
              width: 200,
              render: (_: unknown, record: CampaignPromoCodeDto) =>
                record.campaign?.name ?? "-",
            },
          ]
        : []),
      {
        title: "Legacy",
        dataIndex: "legacy",
        key: "legacy",
        width: 100,
        render: (legacy: boolean) =>
          legacy ? <Tag>Yes</Tag> : <Tag color="green">No</Tag>,
      },
      {
        title: "Prefix",
        key: "prefix",
        width: 120,
        render: (_, record) => {
          if (record.legacy) {
            return "-";
          }
          const prefix =
            parsePrefixedPromoCode(record.code) ??
            record.campaign?.codePrefix ??
            null;
          return prefix ? <Tag color="blue">{prefix}</Tag> : "-";
        },
      },
      {
        title: "Code",
        dataIndex: "code",
        key: "code",
        width: 260,
        render: (code: string) => (
          <Typography.Text
            className={styles.codeCell}
            copyable={{
              text: code,
              tooltips: ["Copy code", "Copied"],
            }}
          >
            {code}
          </Typography.Text>
        ),
      },
      {
        title: "Status",
        key: "status",
        width: 120,
        render: (_, r) =>
          r.userId ? (
            <Tag color="#009E60">Used</Tag>
          ) : (
            <Tag color="#f5222d">Unused</Tag>
          ),
      },
      {
        title: "Claimed by",
        key: "user",
        width: 220,
        render: (_: unknown, record: CampaignPromoCodeDto) => {
          if (!record.userId) {
            return "-";
          }
          const { user } = record;
          return `${user?.nickname ?? "N/A"} (${user?.phoneNumber ?? "N/A"})`;
        },
      },
      {
        title: "CPA (USD)",
        dataIndex: "cpaUsdAtRedemption",
        key: "cpaUsdAtRedemption",
        width: 140,
        render: (v: string | null) =>
          v != null ? `$${Number(v).toFixed(2)}` : "-",
      },
      {
        title: "Redeemed At",
        dataIndex: "redeemedAt",
        key: "redeemedAt",
        width: 280,
        render: (v: string | null) => (
          <span className={styles.dateCell}>{formatTableDateTime(v)}</span>
        ),
      },
      {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 280,
        render: (v: string) => (
          <span className={styles.dateCell}>{formatTableDateTime(v)}</span>
        ),
      },
    ],
    [campaignFilter],
  );

  const onTableChange = ({ current, pageSize }: TablePaginationConfig) => {
    setPage(current || 1);
    if (pageSize !== currentPageSize) {
      setPageSize(pageSize || 20);
      setPage(1);
    }
  };

  const updateContainerHeight = () => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.offsetHeight - 140);
    }
  };

  useEffect(() => {
    const timer = setTimeout(updateContainerHeight, 100);
    window.addEventListener("resize", updateContainerHeight);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateContainerHeight);
    };
  }, [campaignFilter, statusFilter, redeemedFrom, redeemedTo, promoList?.total]);

  const resetFilters = () => {
    setPage(1);
    setSearchParams({});
  };

  const onCampaignFilterChange = (value: string) => {
    setPage(1);
    updateSearchParams({ campaignId: value === ALL ? null : value });
  };

  const handleGenerateSuccess = (
    codes: CampaignPromoCodeDto[],
    generatedCampaignId: string,
  ) => {
    void message.success(`Generated ${codes.length} promo code(s)`);
    if (generatedCampaignId !== selectedCampaignId) {
      onCampaignFilterChange(generatedCampaignId);
    } else {
      queryClient.invalidateQueries(["campaign-promo-codes"]);
    }
  };

  return (
    <Layout className={styles.tableLayout}>
      {generateModalOpen ? (
        <GeneratePromoCodesModal
          open={generateModalOpen}
          campaigns={campaigns}
          defaultCampaignId={selectedCampaignId}
          onClose={() => setGenerateModalOpen(false)}
          onSuccess={handleGenerateSuccess}
        />
      ) : null}

      <Row
        style={{
          height: 32,
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Col
          span={7}
          style={{
            display: "flex",
            alignItems: "center",
            rowGap: 5,
            columnGap: 5,
            height: "100%",
          }}
        >
          <Typography.Title style={{ margin: 0 }} level={5}>
            Promo codes ({promoList?.total ?? 0})
          </Typography.Title>

          <Button type="primary" onClick={() => setGenerateModalOpen(true)}>
            Generate codes
          </Button>
          <Button
            loading={exportCsvMutation.isLoading}
            onClick={() => exportCsvMutation.mutate()}
          >
            Export CSV
          </Button>
        </Col>
        <Col>
          <Form.Item className="search-input-container">
            <Select
              style={{ minWidth: 220 }}
              value={campaignFilter}
              onChange={onCampaignFilterChange}
              options={campaignOptions}
            />
            <Select
              style={{ minWidth: 160 }}
              value={statusFilter}
              onChange={(value) => {
                setPage(1);
                updateSearchParams({ status: value === ALL ? null : value });
              }}
              options={statusOptions}
            />
            <RangePicker
              allowClear
              className={styles.redeemedRangeFilter}
              value={redeemedRange}
              onChange={(dates) => {
                setPage(1);
                if (dates?.[0] && dates[1]) {
                  updateSearchParams({
                    redeemedFrom: dates[0].format("YYYY-MM-DD"),
                    redeemedTo: dates[1].format("YYYY-MM-DD"),
                  });
                } else {
                  updateSearchParams({
                    redeemedFrom: null,
                    redeemedTo: null,
                  });
                }
              }}
            />
            <Button type="primary" onClick={resetFilters}>
              Reset
            </Button>
          </Form.Item>
        </Col>
      </Row>

      <Layout.Content
        ref={containerRef}
        className={styles.content}
        style={{ backgroundColor: "white" }}
      >
        <Table
          rowKey="id"
          className={styles.promoTable}
          loading={isFetching}
          columns={columns}
          dataSource={promoList?.promoCodes ?? []}
          onChange={onTableChange}
          pagination={{
            current: page,
            total: promoList?.total ?? 0,
            pageSize: currentPageSize,
            showSizeChanger: true,
          }}
          size="small"
          scroll={{ y: containerHeight, x: "max-content" }}
        />
      </Layout.Content>
    </Layout>
  );
};

export default CampaignPromoCodes;
